import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, CheckCircle2, Heart, ShieldCheck, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const translations = {
  en: {
    title: 'Request Help',
    subtitle: 'SVdP Mary, Mother of God Parish',
    tagline: 'Journeying with our neighbors in faith and service',
    serving: 'Serving the Uptown & Edgewater communities of Chicago',
    emergency: 'If this is an emergency, please call 911 or 311 for immediate assistance.',
    personalInfo: 'Your Information',
    firstName: 'First Name',
    lastName: 'Last Name',
    phone: 'Phone Number',
    email: 'Email Address',
    address: 'Street Address',
    city: 'City',
    state: 'State',
    zip: 'ZIP Code',
    natureOfNeed: 'Nature of Need',
    selectTypes: 'Please select all that apply',
    rent: 'Rent',
    utilities: 'Utilities',
    medication: 'Medication',
    ids: 'IDs / Documents',
    transportation: 'Transportation',
    food: 'Food',
    clothing: 'Clothing',
    other: 'Other',
    details: 'Additional Details',
    detailsPlaceholder: 'Please provide any additional information about your situation...',
    submit: 'Submit Request',
    submitting: 'Submitting...',
    thankYou: 'Thank you!',
    confirmationMessage: 'We have received your request and will follow up within 24-48 hours.',
    caseNumber: 'Your reference number is',
    godBless: 'God bless you,',
    parish: 'SVdP Mary Mother of God',
    submitAnother: 'Submit Another Request',
    backToHome: 'Back to Home',
    required: 'Please fill in all required fields'
  },
  es: {
    title: 'Solicitar Ayuda',
    subtitle: 'SVdP María, Madre de Dios',
    tagline: 'Caminando junto a nuestros vecinos en fe y servicio',
    serving: 'Sirviendo a las comunidades de Uptown y Edgewater en Chicago',
    emergency: 'Si esto es una emergencia, llame al 911 o al 311 para asistencia inmediata.',
    personalInfo: 'Su Información',
    firstName: 'Nombre',
    lastName: 'Apellido',
    phone: 'Número de Teléfono',
    email: 'Correo Electrónico',
    address: 'Dirección',
    city: 'Ciudad',
    state: 'Estado',
    zip: 'Código Postal',
    natureOfNeed: 'Naturaleza de la Necesidad',
    selectTypes: 'Por favor seleccione todo lo que aplique',
    rent: 'Alquiler',
    utilities: 'Servicios Públicos',
    medication: 'Medicamentos',
    ids: 'Identificaciones / Documentos',
    transportation: 'Transporte',
    food: 'Comida',
    clothing: 'Ropa',
    other: 'Otro',
    details: 'Detalles Adicionales',
    detailsPlaceholder: 'Por favor proporcione información adicional sobre su situación...',
    submit: 'Enviar Solicitud',
    submitting: 'Enviando...',
    thankYou: '¡Gracias!',
    confirmationMessage: 'Hemos recibido su solicitud y haremos seguimiento dentro de 24-48 horas.',
    caseNumber: 'Su número de referencia es',
    godBless: 'Que Dios te bendiga,',
    parish: 'SVdP María Madre de Dios',
    submitAnother: 'Enviar Otra Solicitud',
    backToHome: 'Volver al Inicio',
    required: 'Por favor complete todos los campos requeridos'
  }
};

const SelfServiceIntake = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState('en');
  const [submitted, setSubmitted] = useState(false);
  const [caseNumber, setCaseNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [boundaryCheck, setBoundaryCheck] = useState(null);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: 'IL',
    zip_code: '',
    request_types: [],
    details: '',
    entry_type: 'self'
  });

  const t = translations[lang];

  // Neighbor reassurance messages (bilingual)
  const reassurance = {
    en: {
      welcome: "We're glad you reached out. Let's take this one step at a time.",
      confidential: "We're asking these questions to get a better picture of how we might journey with you. A volunteer will reach out to review everything together.",
      noDocs: "You don't need to have all documents ready right now — a volunteer will help you through the process.",
      noPromises: "Submitting this form does not guarantee assistance, but your request will be reviewed carefully by our team.",
      followUp: "A volunteer will contact you within 48 hours. There is no cost for this service."
    },
    es: {
      welcome: "Nos alegra que se haya comunicado. Vamos paso a paso.",
      confidential: "Hacemos estas preguntas para entender mejor como podemos acompanarle en este camino. Un voluntario se comunicara con usted para revisar todo juntos.",
      noDocs: "No necesita tener todos los documentos listos ahora — un voluntario le ayudara durante el proceso.",
      noPromises: "Enviar este formulario no garantiza asistencia, pero su solicitud sera revisada cuidadosamente por nuestro equipo.",
      followUp: "Un voluntario se comunicara con usted dentro de 48 horas. No hay costo por este servicio."
    }
  };
  const r = reassurance[lang];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (type) => {
    setFormData(prev => ({
      ...prev,
      request_types: prev.request_types.includes(type)
        ? prev.request_types.filter(t => t !== type)
        : [...prev.request_types, type]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.phone || 
        !formData.address || !formData.city || !formData.zip_code || 
        formData.request_types.length === 0) {
      toast.error(t.required);
      return;
    }

    if (!recaptchaToken) {
      toast.error('Please complete the reCAPTCHA verification');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/intake/submit`, {
        ...formData,
        recaptcha_token: recaptchaToken
      });
      
      if (response.data.success === false && response.data.boundary_check === 'Outside_State') {
        setBoundaryCheck('Outside_State');
        setRejectionMessage(response.data.message);
        setSubmitted(true);
      } else {
        setCaseNumber(response.data.case_number);
        setBoundaryCheck(response.data.boundary_check || 'Inside');
        setSubmitted(true);
        toast.success('Request submitted successfully!');
      }
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Submission failed';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    // Out of state rejection
    if (boundaryCheck === 'Outside_State') {
      return (
        <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
          <div className="w-full max-w-2xl">
            <div className="glass-panel p-8 md:p-12 animate-fade-in text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-500/20 rounded-full mb-6 border border-amber-500/30">
                <svg className="w-10 h-10 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="outside-state-title">
                {lang === 'es' ? 'Fuera de Nuestra Area de Servicio' : 'Outside Our Service Area'}
              </h1>
              <p className="text-lg text-slate-300 mb-6" data-testid="outside-state-message">
                {lang === 'es'
                  ? 'Agradecemos que se haya comunicado con nosotros. Lamentablemente, nuestra conferencia parroquial solo puede brindar asistencia directa a vecinos dentro de nuestra area local en Chicago, Illinois. Le animamos a comunicarse con su consejo local de San Vicente de Paul o visitar www.svdpusa.net para encontrar una conferencia cerca de usted que pueda ayudarle.'
                  : rejectionMessage || 'We appreciate you reaching out. Unfortunately, our parish conference is only able to provide direct assistance to neighbors within our local area in Chicago, Illinois. We encourage you to contact your local St. Vincent de Paul council or visit www.svdpusa.net to find a conference near you that may be able to help.'
                }
              </p>
              <a
                href="https://www.svdpusa.net"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#0072BC] hover:text-[#0072BC]/80 underline mb-8"
                data-testid="svdp-national-link"
              >
                {lang === 'es' ? 'Visitar SVdP Nacional' : 'Visit SVdP National Website'}
              </a>
              <div className="mb-8">
                <p className="text-slate-400">{t.godBless}</p>
                <p className="font-semibold text-white">{t.parish}</p>
              </div>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 rounded-full font-medium transition-colors"
                data-testid="back-home-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                {t.backToHome}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Outside local (in Illinois but not in parish ZIP) — referral
    if (boundaryCheck === 'Outside_Local') {
      return (
        <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
          <div className="w-full max-w-2xl">
            <div className="glass-panel p-8 md:p-12 animate-fade-in text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#0072BC]/20 rounded-full mb-6 border border-[#0072BC]/30">
                <svg className="w-10 h-10 text-[#0072BC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="outside-local-title">
                {t.thankYou}
              </h1>
              <div className="inline-block bg-white/10 backdrop-blur-md px-8 py-5 rounded-2xl border border-white/10 mb-6">
                <p className="text-sm text-slate-400 mb-2">{t.caseNumber}</p>
                <p className="text-4xl font-bold text-[#0072BC]" data-testid="case-number">{caseNumber}</p>
              </div>
              <p className="text-lg text-slate-300 mb-6" data-testid="outside-local-message">
                {lang === 'es'
                  ? 'Gracias por comunicarse con nosotros. Segun la informacion que nos proporciono, parece que se encuentra fuera de los limites inmediatos de nuestra parroquia. Aunque nuestra conferencia atiende a vecinos dentro de nuestra area local, nos gustaria ayudarle a conectarse con una conferencia de San Vicente de Paul mas cercana a usted. Un voluntario se comunicara para compartir opciones y referencias.'
                  : 'Thank you for reaching out to us. Based on the information you provided, it appears you may be outside our immediate parish boundaries. While our conference serves neighbors within our local area, we would like to help connect you with a St. Vincent de Paul conference closer to you. A volunteer will be in touch to discuss options and share any referrals we can offer.'
                }
              </p>
              <div className="mb-8">
                <p className="text-slate-400">{t.godBless}</p>
                <p className="font-semibold text-white">{t.parish}</p>
              </div>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 rounded-full font-medium transition-colors"
                data-testid="back-home-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                {t.backToHome}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Inside boundaries — normal success
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
        <div className="w-full max-w-2xl">
          <div className="glass-panel p-8 md:p-12 animate-fade-in text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-500/20 rounded-full mb-6 border border-emerald-500/30">
              <CheckCircle2 className="w-10 h-10 text-emerald-400" />
            </div>
            
            <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.thankYou}
            </h1>
            
            <p className="text-lg text-slate-300 mb-6">{t.confirmationMessage}</p>
            
            <div className="inline-block bg-white/10 backdrop-blur-md px-8 py-5 rounded-2xl border border-white/10 mb-8" data-testid="success-message">
              <p className="text-sm text-slate-400 mb-2">{t.caseNumber}</p>
              <p className="text-4xl font-bold text-[#0072BC]" data-testid="case-number">{caseNumber}</p>
            </div>

            <div className="emergency-banner mb-8">
              <p className="text-sm">{t.emergency}</p>
            </div>

            <div className="mb-8">
              <p className="text-slate-400">{t.godBless}</p>
              <p className="font-semibold text-white">{t.parish}</p>
            </div>

            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 rounded-full font-medium transition-colors"
              data-testid="back-home-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.backToHome}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #134e4a 100%)' }}>
      <div className="container mx-auto max-w-3xl">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="mb-6 inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back
        </button>

        {/* Language Toggle */}
        <div className="flex justify-end mb-6">
          <div className="inline-flex rounded-full bg-white/5 border border-white/10 p-1" data-testid="language-toggle">
            <button
              onClick={() => setLang('en')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                lang === 'en' ? 'bg-[#0072BC] text-white shadow-lg' : 'text-slate-300 hover:text-white'
              }`}
              data-testid="lang-en-btn"
            >
              English
            </button>
            <button
              onClick={() => setLang('es')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                lang === 'es' ? 'bg-[#0072BC] text-white shadow-lg' : 'text-slate-300 hover:text-white'
              }`}
              data-testid="lang-es-btn"
            >
              Español
            </button>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="flex justify-center mb-6">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/10">
              <img 
                src="/images/svdp-logo.jpg" 
                alt="SVdP Chicago" 
                className="h-16 w-auto"
                data-testid="svdp-logo"
              />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t.title}
          </h1>
          <p className="text-lg text-slate-400">{t.subtitle}</p>
          <p className="text-base text-slate-400 italic mt-1">{t.tagline}</p>
          <p className="text-sm text-slate-500">{t.serving}</p>
        </div>

        {/* Emergency Notice */}
        <div className="emergency-banner mb-8 animate-fade-in delay-100" data-testid="emergency-notice">
          <p>{t.emergency}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="glass-panel p-6 md:p-8 animate-fade-in delay-200" data-testid="intake-form">
          {/* Welcome Reassurance */}
          <div className="mb-8 px-4 py-3 bg-[#0072BC]/10 border border-[#0072BC]/20 rounded-xl flex items-start gap-3" data-testid="neighbor-welcome-msg">
            <Heart className="w-5 h-5 text-[#0072BC] mt-0.5 shrink-0" />
            <p className="text-sm text-slate-300">{r.welcome}</p>
          </div>

          {/* Personal Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 pb-3 border-b border-white/10" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.personalInfo}
            </h2>
            {/* Confidentiality Note */}
            <div className="mb-5 px-3 py-2 bg-white/5 border border-white/5 rounded-lg flex items-start gap-2" data-testid="neighbor-confidential-msg">
              <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              <p className="text-xs text-slate-400">{r.confidential}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.firstName} *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                  required
                  data-testid="first-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.lastName} *</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                  required
                  data-testid="last-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.phone} *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                  required
                  data-testid="phone-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.email} *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.address} *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                  required
                  data-testid="address-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">{t.city} *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                  required
                  data-testid="city-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t.state} *</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                    required
                    data-testid="state-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">{t.zip} *</label>
                  <input
                    type="text"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none"
                    required
                    data-testid="zip-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Nature of Need */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4 pb-3 border-b border-white/10" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.natureOfNeed}
            </h2>
            {/* No docs needed reassurance */}
            <div className="mb-4 px-3 py-2 bg-white/5 border border-white/5 rounded-lg flex items-start gap-2" data-testid="neighbor-nodocs-msg">
              <FileText className="w-4 h-4 text-[#0072BC] mt-0.5 shrink-0" />
              <p className="text-xs text-slate-400">{r.noDocs}</p>
            </div>
            
            <p className="text-slate-400 mb-4 text-sm">{t.selectTypes}</p>

            <div className="grid md:grid-cols-2 gap-3" data-testid="nature-of-need">
              {['rent', 'utilities', 'medication', 'ids', 'transportation', 'food', 'clothing', 'other'].map(type => (
                <label 
                  key={type} 
                  className={`flex items-center space-x-3 p-4 rounded-xl cursor-pointer transition-all border ${
                    formData.request_types.includes(type)
                      ? 'bg-[#0072BC]/20 border-[#0072BC]/50 text-white'
                      : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:border-white/20'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.request_types.includes(type)}
                    onChange={() => handleCheckboxChange(type)}
                    className="w-5 h-5 rounded bg-white/10 border-white/20 text-[#0072BC] focus:ring-[#0072BC] focus:ring-offset-0"
                    data-testid={`checkbox-${type}`}
                  />
                  <span className="font-medium">{t[type]}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Details */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-slate-300 mb-2">{t.details}</label>
            <textarea
              name="details"
              value={formData.details}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-slate-500 focus:border-[#0072BC] focus:ring-1 focus:ring-[#0072BC] transition-all outline-none min-h-[120px] resize-y"
              placeholder={t.detailsPlaceholder}
              data-testid="details-textarea"
            />
          </div>

          {/* Pre-Submit Reassurance */}
          <div className="mb-6 px-4 py-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl space-y-2" data-testid="neighbor-presubmit-msg">
            <p className="text-sm text-slate-300 flex items-start gap-2">
              <Heart className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              <span>{r.followUp}</span>
            </p>
            <p className="text-xs text-slate-400 ml-6">{r.noPromises}</p>
          </div>

          {/* reCAPTCHA and Submit */}
          <div className="text-center">
            {RECAPTCHA_SITE_KEY && (
              <div className="flex justify-center mb-6">
                <div className="bg-white/5 p-3 rounded-xl border border-white/10">
                  <ReCAPTCHA
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(token) => setRecaptchaToken(token || '')}
                    onExpired={() => setRecaptchaToken('')}
                    theme="dark"
                    data-testid="recaptcha"
                  />
                </div>
              </div>
            )}
            <button
              type="submit"
              disabled={loading || (RECAPTCHA_SITE_KEY && !recaptchaToken)}
              className="inline-flex items-center gap-2 bg-[#0072BC] hover:bg-[#005a9e] text-white rounded-full px-10 py-4 font-medium text-lg transition-all shadow-lg shadow-blue-900/30 hover:shadow-blue-900/50 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t.submitting}
                </>
              ) : (
                t.submit
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SelfServiceIntake;
